package dev.youndie.site.model

/** The name that goes in `article:author` and in every schema.org `author`. */
const val AUTHOR_NAME = "Pavel Votyakov"

/**
 * Serialises one schema.org object.
 *
 * Written by hand rather than with kotlinx.serialization because the shapes here are
 * small and fixed, and because the escaping below is the whole point: this string is
 * dropped inside a `<script>` element, where a `<` in any value would otherwise be
 * read as the start of a tag and end the block early.
 */
private fun jsonString(value: String): String {
    val out = StringBuilder("\"")
    value.forEach { c ->
        when {
            c == '"' -> out.append("\\\"")
            c == '\\' -> out.append("\\\\")
            c == '\n' -> out.append("\\n")
            c == '\r' -> out.append("\\r")
            c == '\t' -> out.append("\\t")
            // `<` and `&` never need escaping in JSON, and always need it here.
            c == '<' -> out.append("\\u003c")
            c == '>' -> out.append("\\u003e")
            c == '&' -> out.append("\\u0026")
            c.code < 0x20 -> out.append("\\u" + c.code.toString(16).padStart(4, '0'))
            else -> out.append(c)
        }
    }
    return out.append('"').toString()
}

/** Renders `{ "key": value }`, skipping any entry whose value is null or blank. */
private fun jsonObject(vararg entries: Pair<String, String?>): String =
    entries
        .filter { !it.second.isNullOrBlank() }
        .joinToString(",", "{", "}") { (key, value) -> "${jsonString(key)}:${value}" }

private fun jsonArray(values: List<String>): String = values.joinToString(",", "[", "]")

private val authorNode: String
    get() = jsonObject(
        "@type" to jsonString("Person"),
        "name" to jsonString(AUTHOR_NAME),
        "url" to jsonString(SITE_URL),
    )

/** The site itself, for the home page. */
fun personJsonLd(description: String, sameAs: List<String>): String = jsonObject(
    "@context" to jsonString("https://schema.org"),
    "@type" to jsonString("Person"),
    "name" to jsonString(AUTHOR_NAME),
    "url" to jsonString(SITE_URL),
    "jobTitle" to jsonString("Kotlin developer"),
    "description" to jsonString(description),
    "sameAs" to jsonArray(sameAs.map { jsonString(it) }),
)

/**
 * One post. [url] is this page even when the canonical points at the original
 * elsewhere — the object describes the document a crawler is currently reading.
 */
fun blogPostingJsonLd(
    headline: String,
    description: String?,
    url: String,
    datePublished: String,
    image: String?,
): String = jsonObject(
    "@context" to jsonString("https://schema.org"),
    "@type" to jsonString("BlogPosting"),
    "headline" to jsonString(headline),
    "description" to description?.let { jsonString(it) },
    "mainEntityOfPage" to jsonString(url),
    "url" to jsonString(url),
    "datePublished" to jsonString(datePublished),
    "dateModified" to jsonString(datePublished),
    "image" to image?.let { jsonString(if (it.startsWith("http")) it else SITE_URL + it) },
    "author" to authorNode,
    "inLanguage" to jsonString("en"),
)

/**
 * One library. `SoftwareSourceCode` rather than `SoftwareApplication`: these are
 * things you depend on from a build file, not things anybody installs and runs.
 */
fun softwareSourceCodeJsonLd(
    name: String,
    description: String,
    url: String,
    repository: String,
    languages: List<String>,
): String = jsonObject(
    "@context" to jsonString("https://schema.org"),
    "@type" to jsonString("SoftwareSourceCode"),
    "name" to jsonString(name),
    "description" to jsonString(description),
    "url" to jsonString(url),
    "codeRepository" to jsonString(repository),
    "programmingLanguage" to jsonArray(languages.map { jsonString(it) }),
    "license" to jsonString("https://opensource.org/licenses/MIT"),
    "author" to authorNode,
)
